// Top-bar Friends widget. Shows a small icon with a count of friends
// currently online; opens a popover listing friends with online dots
// plus an "Add a friend" link.

import { css, html } from 'lit';
import { api } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { router } from '../../lib/router.js';
import { popoverState } from '../../state/popover-state.js';
import { presenceState } from '../../state/presence-state.js';

const POPOVER_ID = 'friends';

class FriendsWidget extends BaseComponent {
  static properties = {
    _friends: { type: Array, state: true },
    _open: { type: Boolean, state: true },
    _loading: { type: Boolean, state: true },
    _onlineCount: { type: Number, state: true },
  };

  static styles = css`
    :host {
      position: relative;
      display: inline-flex;
    }

    .trigger {
      position: relative;
      background: transparent;
      border: none;
      color: rgba(255, 255, 255, 0.7);
      padding: 4px 6px;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      font-size: 1.4rem;
      line-height: 1;
      transition: color 0.15s;
    }

    .trigger:hover,
    .trigger:focus {
      color: rgba(255, 255, 255, 1);
      outline: none;
    }

    .menu-panel {
      position: absolute;
      top: 100%;
      right: 0;
      margin-top: 4px;
      width: 280px;
      max-height: 360px;
      overflow-y: auto;
      background: #1a1a2a;
      border: 1px solid rgba(255, 255, 255, 0.15);
      border-radius: 6px;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
      z-index: 1500;
      color: rgba(255, 255, 255, 0.9);
    }

    .menu-header {
      padding: 8px 12px;
      font-size: 0.75rem;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: rgba(255, 255, 255, 0.5);
      border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    }

    .friend-row {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 12px;
      font-size: 0.85rem;
    }

    .dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      flex-shrink: 0;
    }

    .dot.online {
      background: #28a745;
    }

    .dot.offline {
      background: #555;
    }

    .friend-name {
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .empty {
      padding: 16px 12px;
      text-align: center;
      font-size: 0.85rem;
      color: rgba(255, 255, 255, 0.55);
    }

    .footer {
      border-top: 1px solid rgba(255, 255, 255, 0.08);
      padding: 8px 12px;
    }

    .footer button {
      background: transparent;
      border: none;
      color: #6db4ff;
      cursor: pointer;
      font-size: 0.85rem;
      padding: 0;
    }

    .footer button:hover {
      text-decoration: underline;
    }
  `;

  constructor() {
    super();
    /** @type {Array<{id: string, username: string, email_hash?: string, picture?: string}>} */
    this._friends = [];
    this._open = false;
    this._loading = false;
    this._onlineCount = 0;
    this._handleDocumentClick = this._handleDocumentClick.bind(this);
    /** @type {(() => void) | null} */
    this._unsubscribePresence = null;
    /** @type {(() => void) | null} */
    this._unsubscribePopover = null;
  }

  async connectedCallback() {
    await super.connectedCallback();
    this._unsubscribePresence = presenceState.subscribe(() => {
      this._onlineCount = presenceState.onlineCount;
      this.requestUpdate();
    });
    this._unsubscribePopover = popoverState.subscribe((openId) => {
      this._open = openId === POPOVER_ID;
    });
    presenceState.refetch();
    document.addEventListener('click', this._handleDocumentClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._unsubscribePresence?.();
    this._unsubscribePresence = null;
    this._unsubscribePopover?.();
    this._unsubscribePopover = null;
    document.removeEventListener('click', this._handleDocumentClick);
  }

  /** @param {MouseEvent} e */
  _handleDocumentClick(e) {
    if (!this._open) return;
    const path = e.composedPath();
    if (!path.includes(this)) popoverState.close(POPOVER_ID);
  }

  async _toggle(e) {
    e.stopPropagation();
    popoverState.toggle(POPOVER_ID);
    // Always reload on open so deletes / adds elsewhere reflect here.
    if (popoverState.openId === POPOVER_ID) {
      await this._loadFriends();
    }
  }

  async _loadFriends() {
    this._loading = true;
    try {
      this._friends = await api.getFriends();
    } catch (err) {
      console.warn('[friends-widget] failed to load friends', err);
      this._friends = [];
    } finally {
      this._loading = false;
    }
  }

  _goAddFriend() {
    popoverState.close(POPOVER_ID);
    router.navigate('/friends');
  }

  render() {
    const onlineCount = this._onlineCount;
    return html`
      <button class="trigger" @click="${this._toggle}" title="Friends" aria-label="Friends">
        <i class="bi bi-people-fill"></i>
        ${
          onlineCount > 0
            ? html`<span class="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-success" style="font-size: 0.6rem;">${onlineCount}</span>`
            : ''
        }
      </button>
      ${
        this._open
          ? html`
            <div class="menu-panel" @click="${(/** @type {Event} */ e) => e.stopPropagation()}">
              <div class="menu-header">Friends (${onlineCount} online)</div>
              ${
                this._loading
                  ? html`<div class="empty">Loading…</div>`
                  : this._friends.length === 0
                    ? html`<div class="empty">No friends yet.</div>`
                    : this._friends.map(
                        (f) => html`
                        <div class="friend-row">
                          <span class="dot ${presenceState.isOnline(f.id) ? 'online' : 'offline'}"></span>
                          <span class="friend-name">${f.username}</span>
                        </div>
                      `,
                      )
              }
              <div class="footer">
                <button @click="${this._goAddFriend}">Add a friend →</button>
              </div>
            </div>
          `
          : ''
      }
    `;
  }
}

customElements.define('friends-widget', FriendsWidget);
